import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  CaseDetail,
  CasePage,
  CategoryOut,
  ClaudeStatusOut,
  DeviceOut,
  DocumentDetail,
  DocumentPage,
  LLMProviderOut,
  LmLinkOut,
  MailAccountOut,
  ReminderOut,
  SearchResponseOut,
  SystemStatus,
} from './models'

/**
 * One place for every server interaction. Query keys are hierarchical so a mutation can
 * invalidate exactly the branch it touched - the same convention across the whole UI.
 */

export const keys = {
  setup: ['setup'] as const,
  me: ['me'] as const,
  documents: (filters: Record<string, unknown> = {}) => ['documents', filters] as const,
  document: (id: string) => ['document', id] as const,
  cases: (filters: Record<string, unknown> = {}) => ['cases', filters] as const,
  case: (id: string) => ['case', id] as const,
  reminders: (state: string) => ['reminders', state] as const,
  search: (query: string, cursor: string | null) => ['search', query, cursor] as const,
  status: ['system', 'status'] as const,
  jobs: (state?: string) => ['system', 'jobs', state ?? 'all'] as const,
  settings: ['settings'] as const,
  mailAccounts: ['settings', 'mail-accounts'] as const,
  folders: (id: string) => ['settings', 'mail-accounts', id, 'folders'] as const,
  providers: ['settings', 'providers'] as const,
  lmlink: ['settings', 'lmlink'] as const,
  claude: ['settings', 'claude'] as const,
  categories: ['settings', 'categories'] as const,
  devices: ['devices'] as const,
  parties: ['parties'] as const,
}

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error) throw result.error
  return result.data as T
}

export function useSetupState() {
  return useQuery({
    queryKey: keys.setup,
    queryFn: async () => unwrap(await api.GET('/api/setup', {})),
    retry: false,
  })
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => unwrap(await api.GET('/api/auth/me', {})),
    enabled,
    retry: false,
  })
}

export function useSystemStatus(refetchMs = 15000) {
  return useQuery<SystemStatus>({
    queryKey: keys.status,
    queryFn: async () => unwrap(await api.GET('/api/system/status', {})),
    refetchInterval: refetchMs,
  })
}

export function useDocuments(params: Record<string, string | number | undefined> = {}) {
  return useQuery<DocumentPage>({
    queryKey: keys.documents(params),
    queryFn: async () =>
      unwrap(await api.GET('/api/documents', { params: { query: params as never } })),
  })
}

export function useDocument(id: string | undefined) {
  return useQuery<DocumentDetail>({
    queryKey: keys.document(id ?? ''),
    queryFn: async () =>
      unwrap(await api.GET('/api/documents/{document_id}', { params: { path: { document_id: id as string } } })),
    enabled: Boolean(id),
  })
}

export function useCorrectDocument(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      unwrap(
        await api.PATCH('/api/documents/{document_id}', {
          params: { path: { document_id: id } },
          body: body as never,
        }),
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.document(id) })
      client.invalidateQueries({ queryKey: ['documents'] })
      client.invalidateQueries({ queryKey: ['cases'] })
    },
  })
}

export function useQuarantine(state: string) {
  return useQuery<DocumentPage>({
    queryKey: ['quarantine', state],
    queryFn: async () =>
      unwrap(await api.GET('/api/quarantine', { params: { query: { state } as never } })),
  })
}

export function useSpamDecision() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'spam' | 'ok' }) =>
      unwrap(
        await api.POST('/api/documents/{document_id}/spam', {
          params: { path: { document_id: id } },
          body: { decision },
        }),
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['quarantine'] })
      client.invalidateQueries({ queryKey: ['documents'] })
      client.invalidateQueries({ queryKey: ['document'] })
    },
  })
}

export function useCases(params: Record<string, string | number | undefined> = {}) {
  return useQuery<CasePage>({
    queryKey: keys.cases(params),
    queryFn: async () => unwrap(await api.GET('/api/cases', { params: { query: params as never } })),
  })
}

export function useCase(id: string | undefined) {
  return useQuery<CaseDetail>({
    queryKey: keys.case(id ?? ''),
    queryFn: async () =>
      unwrap(await api.GET('/api/cases/{case_id}', { params: { path: { case_id: id as string } } })),
    enabled: Boolean(id),
  })
}

export function useCaseActions(caseId: string) {
  const client = useQueryClient()
  const invalidate = () => {
    client.invalidateQueries({ queryKey: ['cases'] })
    client.invalidateQueries({ queryKey: keys.case(caseId) })
    client.invalidateQueries({ queryKey: ['documents'] })
  }
  return {
    merge: useMutation({
      mutationFn: async (sourceCaseId: string) =>
        unwrap(
          await api.POST('/api/cases/{case_id}/merge', {
            params: { path: { case_id: caseId } },
            body: { source_case_id: sourceCaseId },
          }),
        ),
      onSuccess: invalidate,
    }),
    split: useMutation({
      mutationFn: async (documentId: string) =>
        unwrap(await api.POST('/api/cases/split', { body: { document_id: documentId } })),
      onSuccess: invalidate,
    }),
    move: useMutation({
      mutationFn: async (documentId: string) =>
        unwrap(
          await api.POST('/api/cases/{case_id}/documents', {
            params: { path: { case_id: caseId } },
            body: { document_id: documentId },
          }),
        ),
      onSuccess: invalidate,
    }),
    setStatus: useMutation({
      mutationFn: async (status: string) =>
        unwrap(
          await api.PATCH('/api/cases/{case_id}', {
            params: { path: { case_id: caseId } },
            body: { status } as never,
          }),
        ),
      onSuccess: invalidate,
    }),
  }
}

export function useSearch(query: string, cursor: string | null, enabled: boolean) {
  return useQuery<SearchResponseOut>({
    queryKey: keys.search(query, cursor),
    queryFn: async () =>
      unwrap(await api.POST('/api/search', { body: { query, cursor, limit: 20, highlights: true } })),
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useReminders(state: string) {
  return useQuery<ReminderOut[]>({
    queryKey: keys.reminders(state),
    queryFn: async () =>
      unwrap(await api.GET('/api/reminders', { params: { query: { state: state as never } } })),
  })
}

export function useReminderActions() {
  const client = useQueryClient()
  const invalidate = () => client.invalidateQueries({ queryKey: ['reminders'] })
  return {
    complete: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.POST('/api/reminders/{reminder_id}/complete', {
            params: { path: { reminder_id: id } },
          }),
        ),
      onSuccess: invalidate,
    }),
    snooze: useMutation({
      mutationFn: async ({ id, until }: { id: string; until: string }) =>
        unwrap(
          await api.POST('/api/reminders/{reminder_id}/snooze', {
            params: { path: { reminder_id: id } },
            body: { until },
          }),
        ),
      onSuccess: invalidate,
    }),
    create: useMutation({
      mutationFn: async (body: { title: string; due_at: string; body?: string }) =>
        unwrap(await api.POST('/api/reminders', { body: body as never })),
      onSuccess: invalidate,
    }),
  }
}

export function useSettings() {
  return useQuery<Record<string, unknown>>({
    queryKey: keys.settings,
    queryFn: async () => unwrap(await api.GET('/api/settings', {})),
  })
}

export function useUpdateSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (values: Record<string, unknown>) =>
      unwrap(await api.PATCH('/api/settings', { body: { values } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.settings }),
  })
}

export function useMailAccounts() {
  return useQuery<MailAccountOut[]>({
    queryKey: keys.mailAccounts,
    queryFn: async () => unwrap(await api.GET('/api/settings/mail-accounts', {})),
  })
}

export function useMailAccountActions() {
  const client = useQueryClient()
  const invalidate = () => client.invalidateQueries({ queryKey: keys.mailAccounts })
  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        unwrap(await api.POST('/api/settings/mail-accounts', { body: body as never })),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        unwrap(
          await api.PATCH('/api/settings/mail-accounts/{account_id}', {
            params: { path: { account_id: id } },
            body: body as never,
          }),
        ),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.DELETE('/api/settings/mail-accounts/{account_id}', {
            params: { path: { account_id: id } },
          }),
        ),
      onSuccess: invalidate,
    }),
    test: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.POST('/api/settings/mail-accounts/{account_id}/test', {
            params: { path: { account_id: id } },
          }),
        ),
    }),
    sync: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.POST('/api/settings/mail-accounts/{account_id}/sync', {
            params: { path: { account_id: id } },
          }),
        ),
      onSuccess: invalidate,
    }),
  }
}

export function useProviders() {
  return useQuery<LLMProviderOut[]>({
    queryKey: keys.providers,
    queryFn: async () => unwrap(await api.GET('/api/settings/llm/providers', {})),
  })
}

export function useProviderActions() {
  const client = useQueryClient()
  const invalidate = () => client.invalidateQueries({ queryKey: keys.providers })
  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        unwrap(await api.POST('/api/settings/llm/providers', { body: body as never })),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        unwrap(
          await api.PATCH('/api/settings/llm/providers/{provider_id}', {
            params: { path: { provider_id: id } },
            body: body as never,
          }),
        ),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.DELETE('/api/settings/llm/providers/{provider_id}', {
            params: { path: { provider_id: id } },
          }),
        ),
      onSuccess: invalidate,
    }),
    test: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.POST('/api/settings/llm/providers/{provider_id}/test', {
            params: { path: { provider_id: id } },
          }),
        ),
      onSuccess: invalidate,
    }),
  }
}

export function useLmLink() {
  const client = useQueryClient()
  const status = useQuery<LmLinkOut>({
    queryKey: keys.lmlink,
    queryFn: async () => unwrap(await api.GET('/api/settings/llm/lmlink/status', {})),
    retry: false,
    refetchInterval: 10000,
  })
  const connect = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/settings/llm/lmlink/connect', {})),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.lmlink }),
  })
  const disconnect = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/settings/llm/lmlink/disconnect', {})),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.lmlink }),
  })
  return { status, connect, disconnect }
}

export function useClaudeStatus() {
  return useQuery<ClaudeStatusOut>({
    queryKey: keys.claude,
    queryFn: async () => unwrap(await api.GET('/api/settings/llm/claude/status', {})),
    retry: false,
  })
}

export function useCategories() {
  return useQuery<CategoryOut[]>({
    queryKey: keys.categories,
    queryFn: async () => unwrap(await api.GET('/api/settings/categories', {})),
  })
}

export function useDevices() {
  return useQuery<DeviceOut[]>({
    queryKey: keys.devices,
    queryFn: async () => unwrap(await api.GET('/api/devices', {})),
  })
}

export function useRevokeDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.DELETE('/api/devices/{device_id}', { params: { path: { device_id: id } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.devices }),
  })
}

export function useJobs(state?: string) {
  return useQuery({
    queryKey: keys.jobs(state),
    queryFn: async () =>
      unwrap(
        await api.GET('/api/system/jobs', {
          params: { query: (state ? { state } : {}) as never },
        }),
      ),
    refetchInterval: 10000,
  })
}

export function useSystemActions() {
  const client = useQueryClient()
  return {
    reprocess: useMutation({
      mutationFn: async (documentId?: string) =>
        unwrap(
          await api.POST('/api/system/reprocess', {
            params: { query: (documentId ? { document_id: documentId } : {}) as never },
          }),
        ),
      onSuccess: () => client.invalidateQueries({ queryKey: ['system'] }),
    }),
    retryJob: useMutation({
      mutationFn: async (id: string) =>
        unwrap(
          await api.POST('/api/system/jobs/{job_id}/retry', { params: { path: { job_id: id } } }),
        ),
      onSuccess: () => client.invalidateQueries({ queryKey: ['system'] }),
    }),
    requeueStale: useMutation({
      mutationFn: async () => unwrap(await api.POST('/api/system/jobs/requeue-stale', {})),
      onSuccess: () => client.invalidateQueries({ queryKey: ['system'] }),
    }),
  }
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { current_password: string; new_password: string }) =>
      unwrap(await api.POST('/api/auth/password', { body })),
  })
}
